// Wires the QR code size calculator — pure arithmetic, no QR library. Applies
// the 10:1 distance-to-size rule of thumb (a code reads from roughly 10× its
// width) with 20% headroom, and reports the minimum print size.
//
// The 10:1 rule is the size for a code that holds a short link. A code that
// holds more has more modules, and at the same width each one is smaller, so
// the answer scales by how many modules the printed image is across compared
// with that baseline. It also never goes below the floor a phone held close
// can read (lib/tools/density.js), which is what decides the size of a code
// on a business card.
import { printModules, minPrintMm, ceil1 } from "./density"

const TO_CM = { m: 100, cm: 1, ft: 30.48, in: 2.54 }

// A minimum is rounded up, never down: one decimal place under 10, whole
// numbers from there.
function fmt(n) {
  return n >= 10 ? Math.ceil(n - 1e-9) : ceil1(n)
}

const RULE = "Based on the 10:1 rule (a code reads from about 10× its width) with 20% headroom."
const CARE = "Bigger is always safe. Keep high contrast and don't crop the quiet zone."

export function setupQrSizeCalculator() {
  const widget = document.querySelector('[data-qr-widget="calculator"]')
  if (!widget) return

  const distance = widget.querySelector("[data-qrsize-distance]")
  const unit = widget.querySelector("[data-qrsize-unit]")
  const content = widget.querySelector("[data-qrsize-content]")
  const result = widget.querySelector("[data-qrsize-result]")
  const output = widget.querySelector("[data-qrsize-output]")
  const note = widget.querySelector("[data-qrsize-note]")
  const empty = widget.querySelector("[data-qrsize-empty]")
  const baseline = parseInt(widget.dataset.qrsizeBaseline, 10) || 29

  function update() {
    const d = parseFloat(distance.value)
    if (!d || d <= 0) {
      result.hidden = true
      if (empty) empty.hidden = false
      return
    }
    const modules = parseInt(content?.value, 10) || baseline
    const density = printModules(modules) / printModules(baseline)
    const cm = d * (TO_CM[unit.value] || 1)
    const byDistanceCm = (cm / 10) * 1.2 * density // 10:1 rule + 20% headroom, scaled for density
    const floorCm = minPrintMm(modules) / 10
    const minCm = Math.max(byDistanceCm, floorCm)
    const inches = minCm / 2.54
    output.textContent = `${fmt(minCm)} cm  ·  ${fmt(inches)} in`

    if (floorCm > byDistanceCm) {
      note.textContent = `At this distance the print is the limit, not the distance. A code with ${modules} squares on a side needs this width for a phone held close to read it. ${CARE}`
    } else if (modules > baseline) {
      note.textContent = `A code that holds this has about ${modules} squares on a side, against ${baseline} for a short link, so it needs ${density.toFixed(1)} times the width at the same distance. ${RULE} ${CARE}`
    } else {
      note.textContent = `${RULE} ${CARE}`
    }
    result.hidden = false
    if (empty) empty.hidden = true
  }

  distance.addEventListener("input", update)
  unit.addEventListener("change", update)
  content?.addEventListener("change", update)
}
